import { ReactNode, useMemo, useCallback } from 'react';
import { useQueueContext } from '../../../Queue/QueueContext';
import { prefixClass } from 'lib/utils';
import { IconPromote, IconForward, IconMention } from 'components/Icons';
import './MobileCatchUp.scss';

/**
 * MobileCatchUp - Dashboard/stats screen for mobile KSQ2
 *
 * Mobile equivalent of FilterPanel. Shows:
 * - Chart (passed as prop)
 * - Quick stats (blockers, open requests, @mentions)
 * - Stats are clickable to navigate to filtered To-Dos
 *
 * @see RN equivalent: ks-mobile-app/src/screens/Home/HomeScreen.tsx
 * @see Desktop equivalent: KSQ2/Panels/FilterPanel/FilterPanel.tsx
 */

interface StatItem {
  key: string;
  icon: ReactNode;
  label: string;
  count: number;
  alert?: boolean;
  onClick?: () => void;
}

interface MobileCatchUpProps {
  /** Chart component to display */
  chart?: ReactNode;
  /** Callback to switch to To-Dos tab with filter */
  onNavigateToTodos?: (filter?: string) => void;
}

export const MobileCatchUp = ({ chart, onNavigateToTodos }: MobileCatchUpProps) => {
  const prefix = prefixClass('ksq2-mobile-catchup');
  const { state } = useQueueContext();
  const { requests, meId } = state;

  // Calculate counts for stats
  const stats = useMemo(() => {
    let blockers = 0;
    let openCount = 0;
    let mentions = 0;

    requests.forEach(request => {
      // Count blockers
      if (request.priority === 'Blocker' && request.status !== 'Closed') {
        blockers++;
      }

      // Count open (assigned to me or my team)
      if (request.status === 'Open') {
        openCount++;
        // Check if I'm mentioned in comments
        if (request.comments?.some(c => c.comment?.includes(`@${meId}`))) {
          mentions++;
        }
      }
    });

    return { blockers, openCount, mentions };
  }, [requests, meId]);

  const handleStatClick = useCallback(
    (filter: string) => {
      if (onNavigateToTodos) {
        onNavigateToTodos(filter);
      }
    },
    [onNavigateToTodos]
  );

  const statItems: StatItem[] = useMemo(
    () => [
      {
        key: 'blockers',
        icon: <IconPromote />,
        label: 'Blockers',
        count: stats.blockers,
        alert: stats.blockers > 0,
        onClick: () => handleStatClick('blockers'),
      },
      {
        key: 'open',
        icon: <IconForward />,
        label: 'Open',
        count: stats.openCount,
        onClick: () => handleStatClick('open'),
      },
      {
        key: 'mentions',
        icon: <IconMention />,
        label: '@Mentions',
        count: stats.mentions,
        onClick: () => handleStatClick('mentions'),
      },
    ],
    [stats, handleStatClick]
  );

  return (
    <div className={prefix()}>
      {/* Chart section */}
      {chart && <div className={prefix('__chart')}>{chart}</div>}

      {/* Stats section */}
      <div className={prefix('__stats')}>
        {statItems.map(item => (
          <button
            key={item.key}
            className={`${prefix('__stat')} ${item.alert ? prefix('__stat--alert') : ''}`}
            onClick={item.onClick}
            type="button"
          >
            <div className={prefix('__stat-icon')}>{item.icon}</div>
            <div className={prefix('__stat-content')}>
              <span className={prefix('__stat-count')}>{item.count}</span>
              <span className={prefix('__stat-label')}>{item.label}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default MobileCatchUp;
